import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiVizRealtimeCloudSaveGenericDatasource } from '../sql-maps/biviz-realtime-cloudsave-generic-sql-map';

export interface RealtimeCloudSaveStatsRateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
    /** Datasource to load metrics from */
    metricsDatasource: any;
    /** Used to set up column that should be present only in nominator of the rate calculation */
    specificDatasourceColumnId: DatasourceColumnId;
    /** Metric used to calculate distinct rate, e.g. distinct users */
    distinctMetric?: { col: string; alias: string };
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class RealtimeCloudSaveStatsRateDataLoaderFactory extends Factory<
    DataLoader,
    RealtimeCloudSaveStatsRateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'RealtimeCloudSaveStatsRateDataLoaderFactory';

    getInstance(options: RealtimeCloudSaveStatsRateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            origQueryContext,
            cancellationToken
        ) {
            // clone queryContext to avoid modifying the original object
            const queryContext = structuredClone(origQueryContext);

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasource = options.metricsDatasource;

                let latestHourSqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_hours_up_to_latest is true, then we should filter hours to be up to the latest hour
                    if (queryContext.settings.realtimeHoursUpToLatest) {
                        const latest_hour = await DataLoader.runQuery(
                            `SELECT EXTRACT(HOUR FROM MAX(time_hour)) AS latest_hour FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                datasource.sqlMap.dataset,
                                datasource.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );

                        return {
                            where: [
                                BiVizRealtimeCloudSaveGenericDatasource.compileWhereSQLHoursUpToLatest(
                                    latest_hour.result[0].latest_hour
                                ),
                            ],
                        };
                    }
                    return;
                })();

                let currentWeekdaySqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_current_weekday is true, then we should filter weekday to be the current weekday
                    if (queryContext.settings.realtimeCurrentWeekday) {
                        const current_weekday = await DataLoader.runQuery(
                            `SELECT FORMAT_DATETIME("%A", MAX(time_hour)) AS current_weekday FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                datasource.sqlMap.dataset,
                                datasource.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );
                        queryContext.settings.realtimeCurrentWeekday =
                            current_weekday.result[0].current_weekday;

                        return {
                            where: [
                                BiVizRealtimeCloudSaveGenericDatasource.compileWhereSQLCurrentWeekday(
                                    current_weekday.result[0].current_weekday
                                ),
                            ],
                        };
                    }
                    return;
                })();

                function processDatasource(qC: IQueryContext) {
                    // identify columns not supported in datasource
                    const datasourceSeparated =
                        DataLoader.separatePresentAndMissingDims(
                            qC.datasourceColumns,
                            datasource.sqlMap.columns
                        );

                    const customSQLTokens: Partial<ICompiledSQLTokens> =
                        mergeSqlTokensArray(
                            [latestHourSqlToken, currentWeekdaySqlToken]
                                // filter out undefined values
                                .filter(
                                    (x) => x
                                ) as Partial<ICompiledSQLTokens>[]
                        );

                    const compiledSQL = datasource.compileSQLStatements(
                        datasourceSeparated.present,
                        qC.settings,
                        options,
                        customSQLTokens
                    );

                    const datasourceColumnsStatus =
                        DataLoader.getMissingColumnsStatus(
                            datasourceSeparated.missing
                        );

                    const tableFullPath = generateTableFullPath(
                        options.gcpProjectId,
                        datasource.sqlMap.dataset,
                        datasource.sqlMap.tableId
                    );

                    return {
                        compiledSQL,
                        datasourceColumnsStatus,
                        tableFullPath,
                    };
                }
                const failsDsProcessed = processDatasource(queryContext);

                const normalizerQueryContext =
                    structuredClone(origQueryContext);
                // find and remove specificDatasourceColumnId in queryContext.datasourceColumns and remove it
                const specificColumn =
                    normalizerQueryContext.datasourceColumns.find(
                        (col) => col.id === options.specificDatasourceColumnId
                    );
                if (specificColumn) {
                    normalizerQueryContext.datasourceColumns =
                        normalizerQueryContext.datasourceColumns.filter(
                            (col) =>
                                col.id !== options.specificDatasourceColumnId
                        );
                }
                const normalizedDsProcessed = processDatasource(
                    normalizerQueryContext
                );

                const distinctMetricColumnName = options.distinctMetric ?? {
                    col: 'user_id',
                    alias: 'users',
                };
                const disintctFailMetricFullAlias = `cnt_fail_${distinctMetricColumnName.alias}`;
                const disintctMetricFullAlias = `cnt_${distinctMetricColumnName.alias}`;

                const failsBase = `
                SELECT *,
                  ${failsDsProcessed.compiledSQL.splitSelect}
                  ${failsDsProcessed.compiledSQL.datasourceSpecificSplitSelect}
                  ${failsDsProcessed.compiledSQL.cohortSplitSelect}
                  ${datasource.sqlMap.columns.ACTIVITY_DATETIME.columnSQL} AS datetime
                FROM \`${failsDsProcessed.tableFullPath}\` ds
                ${failsDsProcessed.compiledSQL.where}
                `;

                const failsQuery = `
                ${SqlComposer.SELECT([
                    failsDsProcessed.compiledSQL.splitGroup,
                    `${failsDsProcessed.compiledSQL.datetimeTrunc(
                        'datetime'
                    )} AS datetime`,
                    `SUM(cnt_fail) AS cnt_fail`,
                    `SUM(${disintctFailMetricFullAlias}) AS ${disintctFailMetricFullAlias}`,
                ])}
                FROM
                (
                  ${SqlComposer.SELECT([
                      `${failsDsProcessed.compiledSQL.splitGroup}`,
                      `datetime`,
                      `COUNT(*) AS cnt_fail`,
                      `COUNT(DISTINCT ${distinctMetricColumnName.col}) AS ${disintctFailMetricFullAlias}`,
                  ])}
                  FROM failsBase
                  ${SqlComposer.GROUP_BY([
                      failsDsProcessed.compiledSQL.splitGroup,
                      'datetime',
                  ])}
                )
                ${SqlComposer.GROUP_BY([
                    failsDsProcessed.compiledSQL.splitGroup,
                    'datetime',
                ])}
                `;

                const normalizerQuery = `
                SELECT
                  ${
                      failsDsProcessed.compiledSQL
                          .splitSelectConcatCohortAndDatasourceSpecificColumn
                  }
                  datetime
                  , cnt_all
                  , ${disintctMetricFullAlias}
                FROM
                (
                  -- aggregate according to chosen datetime aggregation
                  ${SqlComposer.SELECT([
                      `${failsDsProcessed.compiledSQL.cohortSplitGroupAlias}`,
                      `${failsDsProcessed.compiledSQL.datetimeTrunc(
                          'datetime'
                      )} AS datetime`,
                      `SUM(cnt_all) AS cnt_all`,
                      `SUM(${disintctMetricFullAlias}) AS ${disintctMetricFullAlias}`,
                  ])}
                  FROM
                  (
                    -- calculate stats for every day
                    ${SqlComposer.SELECT([
                        normalizedDsProcessed.compiledSQL.splitSelectFn(
                            'cohort_split_group',
                            false
                        ),
                        `${datasource.sqlMap.columns.ACTIVITY_DATETIME.columnSQL} AS datetime`,
                        `COUNT(*) AS cnt_all`,
                        `COUNT(DISTINCT ${distinctMetricColumnName.col}) AS ${disintctMetricFullAlias}`,
                    ])}
                    FROM \`${normalizedDsProcessed.tableFullPath}\` ds
                    ${normalizedDsProcessed.compiledSQL.where}
                    ${SqlComposer.GROUP_BY([
                        normalizedDsProcessed.compiledSQL.cohortSplitGroupAlias,
                        'datetime',
                    ])}
                  )
                  ${SqlComposer.GROUP_BY([
                      normalizedDsProcessed.compiledSQL.cohortSplitGroupAlias,
                      'datetime',
                  ])}
                )
                ${
                    // if split revenue specific columns are selected, then we need to add them to the active users query
                    failsDsProcessed.compiledSQL.datasourceSpecificSplitSelect
                        ? `CROSS JOIN (SELECT DISTINCT ${failsDsProcessed.compiledSQL.datasourceSpecificSplitGroupAlias} FROM failsBase)`
                        : ''
                }
                `;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH failsBase AS
(
    ${failsBase}
)

, fails AS
(
    ${failsQuery}
)

, normalizer AS
(
    ${normalizerQuery}
)

${SqlComposer.SELECT([
    SqlComposer.Prefix('fails', failsDsProcessed.compiledSQL.splitGroup),
    'fails.datetime',
    'fails.cnt_fail',
    `fails.${disintctFailMetricFullAlias}`,
    'normalizer.cnt_all',
    `normalizer.${disintctMetricFullAlias}`,
    'SAFE_DIVIDE(fails.cnt_fail, normalizer.cnt_all) AS failure_rate',
    `SAFE_DIVIDE(fails.${disintctFailMetricFullAlias}, normalizer.${disintctMetricFullAlias}) AS failure_rate_${distinctMetricColumnName.alias}`,
])}
FROM fails
LEFT JOIN normalizer
ON fails.datetime = normalizer.datetime
${
    failsDsProcessed.compiledSQL.splitGroupBy
        ? `AND fails.split_group = normalizer.split_group`
        : ''
}
ORDER BY ${
                    failsDsProcessed.compiledSQL.splitGroupBy !== ''
                        ? `fails.split_group, `
                        : ''
                }fails.datetime ASC             
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'datetime',
                    queryContext.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    // status: pendingDataLoaderResultStatus,
                    status: failsDsProcessed.datasourceColumnsStatus
                        .pendingDataLoaderResultStatus,
                    statusMessage:
                        failsDsProcessed.datasourceColumnsStatus
                            .pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
